import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { AbstractControl, FormArray, FormGroup, UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { Field, Model, Section } from "../../domain/dynamic-form-model"
import { FormControlService } from "../../services/form-control.service";
import { PdfGenerateService } from "../../services/pdf-generate.service";
import { WebsocketService } from "../../services/websocket.service";
import { cloneDeep, isEqual } from "lodash";
import { CommentingWebsocketService } from "../../services/commenting-websocket.service";
import * as UIkit from 'uikit';

@Component({
    selector: 'app-survey',
    templateUrl: 'survey.component.html',
    styleUrls: ['../../assets/theme/form-builder.component.less'],
    providers: [FormControlService, PdfGenerateService, CommentingWebsocketService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class SurveyComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  protected destroyRef = inject(DestroyRef);
  private wsComments = inject(CommentingWebsocketService);
  private ngZone = inject(NgZone);
  protected String = String;

  // Height of the sticky title/tabs/save-bar header, measured so the sidebar
  // (in ChapterComponent) can start its own sticky offset right below it
  // instead of overlapping it — the header's height varies with content
  // (description length, tab wrapping), so this can't be a fixed constant.
  @ViewChild('stickyHeader') stickyHeaderRef!: ElementRef<HTMLElement>;
  protected stickyHeaderHeight = signal(20);
  private stickyHeaderResizeObserver?: ResizeObserver;

  // Counts shown in the header's meta row, matching FormBuilderComponent's
  // own sectionsCount/subsectionsCount/fieldsCount — plain getters (not
  // computed signals) since `model` here is a regular @Input(), not a
  // signal input; Angular re-evaluates these on every change detection pass.
  protected get sectionsCount(): number {
    return this.model?.sections?.length ?? 0;
  }
  protected get subsectionsCount(): number {
    return (this.model?.sections ?? []).reduce((sum, s) => sum + (s.subSections?.length ?? 0), 0);
  }
  protected get fieldsCount(): number {
    return (this.model?.sections ?? []).reduce((sum, s) =>
      sum + (s.subSections ?? []).reduce((sSum, sub) => sSum + (sub.fields?.length ?? 0), 0), 0);
  }

  @Input() payload: any = null; // can't import the specific project class in the lib file
  @Input() model!: Model;
  @Input() subType: string | null = null;
  @Input() userId: string | null = null;
  @Input() enableWebsocket = false;
  @Input() enableCommenting: boolean = false;
  @Input() downloadPDF: boolean = false;
  @Input() mandatoryFieldsText: string = '';
  @Input() tabsHeader: string = '';
  @Input() errorMessage = '';
  @Input() successMessage = '';
  @Input() freeView: boolean = false;

  @Output() valid = new EventEmitter<boolean>();
  @Output() submitForm = new EventEmitter<UntypedFormGroup>();

  chapterChangeMap: Map<string,boolean> = new Map<string, boolean>();
  currentChapter: Section | null = null;
  sortedSurveyAnswers: Object = {};
  ready: boolean = false;
  timeoutId: ReturnType<typeof setTimeout> | null = null;

  editMode: boolean = false;
  readonly: boolean = false;
  validate: boolean = false;

  form = new FormGroup({});
  previousValue: any = {};
  changedField: string[] = [];

  commentingInitialized = false;
  commentsPerSection: Map<string, number> = new Map();

  constructor(private formControlService: FormControlService, private pdfService: PdfGenerateService,
              private router: Router, private wsService: WebsocketService,
              private cd: ChangeDetectorRef) {
  }

  @HostListener('document:focus', ['$event'])
  onFocus(event: FocusEvent): void {
    // console.log("Focus");
  }

  @HostListener('window:blur', ['$event'])
  onBlur(event: FocusEvent): void {
    // console.log('Blur');
  }

  ngOnInit() {
    this.initializeCommenting();

    if (this.enableWebsocket) {
      this.wsService.edit.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (value: any) => {
          // console.log(value);
          // console.log('User id: ' + this.wsService.userId);
          // console.log('Message id: ' + value.sessionId);
          if (value.sessionId === this.wsService.userId) { // Action made by me, ignore
            console.log('It is I');
            this.previousValue = cloneDeep(this.form.value);
            return;
          }
          let ctrl = this.getControl(value.field);
          let path = value.field.split('.');
          if (value.action.type === 'DELETE') { // Remove at position
            const position = path.pop().split('[')[1].split(']')[0];
            if (!ctrl)
              return;
            console.log('Remove at position ' + position);
            // console.log(ctrl.parent as FormArray);
            (ctrl.parent as FormArray).removeAt(+position, {emitEvent: false});
            this.previousValue = cloneDeep(this.form.value);
            return;
          }
          if (value.action.type === 'ADD') { // Push element
            const position = path[path.length - 1].split('[')[1].split(']')[0];
            if ((position.match(/^-?\d+$/)).length === 1) {
              console.log('add element at position ' + position);
              let field = this.getModelData(this.model.sections, path[path.length - 2]);
              path.pop();
              ctrl = this.getControl(path.join('.'));
              (ctrl as FormArray).push(this.formControlService.createField(field), {emitEvent: false});
              this.previousValue = cloneDeep(this.form.value);
            }
            return;
          }
          if (value.action.type === 'MOVE') { // Move element
            const position = path.pop().split('[')[1].split(']')[0];
            if (!ctrl)
              return;
            // console.log(ctrl.parent as FormArray);
            const movedCtrl = (ctrl.parent as FormArray).at(+position);

            console.log('Remove at position ' + position);
            (ctrl.parent as FormArray).removeAt(+position, {emitEvent: false});
            console.log('Insert at position ' + +value.action.index);
            (ctrl.parent as FormArray).insert(+value.action.index, movedCtrl, {emitEvent: false});
            this.previousValue = cloneDeep(this.form.value);
            return;
          }
          if (ctrl) {
            console.log('Setting value from websocket change.');
            // console.log(ctrl);
            if (typeof value.value === 'object' && value.value !== null) {
              console.log('value is object', value.value);
              // handle object creation
              return;
            }
            ctrl.setValue(value.value, {emitEvent: true});
            this.previousValue = cloneDeep(this.form.value);
          } else {
            console.log('This case is redundant, this message should not be seen');
            // console.log(path[path.length-1].split('[')[1].split(']')[0].match(/^-?\d+$/));
            const position = path[path.length - 1].split('[')[1].split(']')[0];
            // console.log(position.match(/^-?\d+$*/));
            if ((position.match(/^-?\d+$/)).length === 1) {
              console.log('add element at position ' + position);
              let field = this.getModelData(this.model.sections, path[path.length - 2]);
              path.pop();
              ctrl = this.getControl(path.join('.'));
              (ctrl as FormArray).push(this.formControlService.createField(field), {emitEvent: false});
              this.previousValue = cloneDeep(this.form.value);
            }
          }
        }
      });

      this.wsService.editDenied.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (message: string) => {
          UIkit.notification({ message: message || 'You no longer have permission to edit this.', status: 'danger', pos: 'top-center', timeout: 5000 });
          this.router.navigateByUrl(this.router.url + '/view');
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.initializeCommenting();

    if (this.router.url.includes('/view')) {
      this.readonly = true;
    } else if (this.router.url.includes('/freeView')) {
      this.freeView = true;
      this.enableCommenting = false;
      this.enableWebsocket = false;
      this.wsComments.setCommenting(false);
    } else if (this.router.url.includes('/validate')) {
      this.validate = true;
    }

    if (this.payload && !(this.readonly || this.freeView)) {
      this.editMode = true;
    }

    if (changes['model']) {
      this.ready = false;

      this.currentChapter = this.model.sections[0];
      this.model.sections = this.model.sections.sort((a, b) => a.order - b.order);
      for (const section of this.model.sections) {
        for (const surveyAnswer in this.payload?.answer) {
          if (section.id === this.payload.answer[surveyAnswer]?.chapterId) {
            this.chapterChangeMap.set(section.id, false);
            this.sortedSurveyAnswers[section.id] = this.payload.answer[surveyAnswer].answer;
            break;
          }
        }
      }
      for (let i = 0; i < this.model.sections.length; i++) {
        if (this.model.sections[i].subSections === null) {
          this.form.addControl(this.model.name, this.formControlService.toFormGroup(this.model.sections, true));
          break;
        }
        if (!this.model.sections[i].subType || this.model.sections[i].subType === this.subType) {
          this.form.addControl(this.model.sections[i].name, this.formControlService.toFormGroup(this.model.sections[i].subSections, true));
        }
      }

      // Patch form
      if (this.payload?.answer) {
        for (let i = 0; i < this.model.sections.length; i++) {
          if (this.payload.answer[this.model.sections[i].name])
            this.prepareForm(this.payload.answer[this.model.sections[i].name], this.model.sections[i].subSections);
        }
        this.form.patchValue(this.payload.answer, {onlySelf: false, emitEvent: false});
        this.previousValue = cloneDeep(this.form.value);
        this.form.markAllAsTouched();
        this.cd.detectChanges();
        if (this.readonly) { // This is bad I should be ashamed
          setTimeout(() => {
            this.form.disable();
            this.form.markAsUntouched();
          }, 2000);
        }

        if (this.editMode) {
          this.form.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef),
            // skip(1), // skip patch value change
            debounceTime(1000),
            distinctUntilChanged((a, b) => isEqual(a, b))
          ).subscribe(changes => {
            this.changedField = this.detectChanges(changes, this.previousValue, '');
            // this.changedField.reverse();
            this.changedField.forEach( change => {
              // console.log(change);
              // console.log(this.getControl(change)?.value);
              let value = this.getControl(change)?.value;
              if (value === undefined) {
                console.log('This message should not be displayed -> check #r3moveField!');
                console.log((this.getControl(change)?.value === undefined));
                value = '#r3moveField!'
              }
              if (this.enableWebsocket) {
                this.wsService.WsEdit({field: change, value: value});
              }
            });
            // if (this.changedField) {
            //   console.log(this.changedField);
            //   // console.log(this.getControl(this.changedField)?.value);
            //   this.wsService.WsEdit({field: this.changedField, value: this.getControl(this.changedField)?.value})
            // }
            this.previousValue = cloneDeep(changes);
          });
        }
      }

      if (this.payload?.validated) {
        this.readonly = true;
        this.validate = false;
      } else if (this.validate) {
        UIkit.modal('#validation-modal').show();
      }
      this.ready = true;

    }
  }

  ngAfterViewInit() {
    if (typeof ResizeObserver !== 'undefined' && this.stickyHeaderRef?.nativeElement) {
      this.stickyHeaderResizeObserver = new ResizeObserver(() => {
        // entries[0].contentRect excludes the element's own padding by
        // definition (it's the content box). #stickyHeader has real padding
        // (20px top), so contentRect.height under-reports its true rendered
        // height — the sidebar's sticky offset came up short by exactly that
        // padding, letting the header overlap the sidebar's first item.
        // getBoundingClientRect() gives the actual border-box height instead.
        const height = this.stickyHeaderRef?.nativeElement?.getBoundingClientRect().height;
        if (height) {
          this.ngZone.run(() => this.stickyHeaderHeight.set(height));
        }
      });
      this.stickyHeaderResizeObserver.observe(this.stickyHeaderRef.nativeElement);
    }
  }

  ngOnDestroy() {
    clearTimeout(this.timeoutId);
    this.wsComments.closeWs();
    this.stickyHeaderResizeObserver?.disconnect();
  }

  initializeCommenting() {
    this.wsComments.setCommenting(this.enableCommenting);
    if (this.enableCommenting && this.payload?.id && !this.commentingInitialized) {
      // console.log('Starting websocket for comments')
      this.wsComments.initializeWebSocketConnection(this.payload.id);
      this.commentingInitialized = true;
    }
  }

  setCommentCount(sectionId: string, count: number) {
    this.commentsPerSection.set(sectionId, count);
  }

  /** Find if any field has changes and get value --> **/
  detectChanges(currentValue: any, previousValue: any, path: string): string[] {
    const changedFields: string[] = [];
    for (const field of Object.keys(currentValue)) {
      const currentPath = path ? `${path}.${field}` : field;

      if (Array.isArray(currentValue[field]) && Array.isArray(previousValue[field])) {
        changedFields.push(...this.detectArrayChanges(currentValue[field], previousValue[field], currentPath));
      } else if (currentValue[field] instanceof Object && previousValue[field] instanceof Object) {
        changedFields.push(...this.detectChanges(currentValue[field], previousValue[field], currentPath));
      } else if (previousValue[field] !== currentValue[field]) {
        changedFields.push(currentPath);
      }
    }
    return changedFields;
  }

  detectArrayChanges(currentArray: any[], previousArray: any[], path: string): string[] {
    const changedFields: string[] = [];
    for (let i = 0; i < Math.max(currentArray.length, previousArray.length); i++) {
      const currentPath = `${path}.[${i}]`;

      if (currentArray[i] instanceof Object && previousArray[i] instanceof Object) {
        changedFields.push(...this.detectChanges(currentArray[i], previousArray[i], currentPath));
      } else if (previousArray[i] !== currentArray[i]) {
        changedFields.push(currentPath);
      }
    }
    return changedFields;
  }

  getControl(path: string): AbstractControl | null {
    const segments = path.split('.');
    let control: AbstractControl | null = this.form;
    for (const segment of segments) {
      if (control instanceof FormGroup) {
        control = control.get(segment);
      } else if (control instanceof FormArray) {
        const index = Number(segment.split('[')[1].split(']')[0]);
        control = control.at(index);
      } else {
        break;
      }
    }
    return control ? control : null;
  }
  /** <-- Find if any field has and get value **/

  validateForm() {
    for (const chapterChangeMapElement of this.chapterChangeMap) {
      if (chapterChangeMapElement[1]) {
        UIkit.modal('#validation-modal').hide();
        this.errorMessage = 'There are unsaved changes, please submit all changes first and then validate.';
        return;
      }
    }
    if (this.form.valid) {
      this.valid.emit(this.form.valid);
    } else {
      UIkit.modal('#validation-modal').hide();
      console.log('Invalid form');
      this.form.markAllAsTouched();
      let str = '';
      for (let key in this.form.value) {
        // console.log(this.form.get('extras.'+key));
        console.log(key + ': '+ this.form.get(key).valid);
        if (!this.form.get(key).valid) {
          str =  str + '\n\t-> ' + key;
        }
        // for (const keyElement in this.form.get(key).value) {
          // console.log(keyElement + ': '+ this.form.get(key+'.'+keyElement).valid);
        // }
      }
      this.errorMessage = 'There are missing fields at chapters ' + str;
    }
  }

  parentSubmit() {
    this.submitForm.emit(this.form);
  }

  showUnsavedChangesPrompt(chapter: Section) {
    if (this.readonly || this.freeView)
      return;
    if (this.chapterChangeMap.get(this.currentChapter.id)) {
      UIkit.modal('#unsaved-changes-modal').show();
    }
    this.currentChapter = chapter;
  }

  getFormGroup(sectionIndex: number): UntypedFormGroup {
    if (this.model.sections[sectionIndex].subSections === null) {
      return this.form.get(this.model.name) as UntypedFormGroup;
    } else
      // console.log(this.form.get(this.survey.sections[sectionIndex].name));
      return this.form.get(this.model.sections[sectionIndex].name) as UntypedFormGroup;
  }

  setChapterChangesMap(chapterId: string[]) {
    if (chapterId[1] === null) {
      this.chapterChangeMap.set(chapterId[0], true);
    } else {
      this.chapterChangeMap.set(chapterId[0], false);
    }
  }

  clearChapterChangesMap() {
    for (const key of this.chapterChangeMap.keys()) {
      this.chapterChangeMap.set(key, false);
    }
  }

  /** create additional fields for arrays if needed --> **/
  prepareForm(answer: Object, fields: Section[], arrayIndex?: number) { // I don't think it will work with greater depth than 2 of array nesting
    for (const [key, value] of Object.entries(answer)) {
      if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
        this.prepareForm(value, fields);
      } else if (Array.isArray(value)) {
        // console.log(value);
        if (value && value?.length > 1) {
          this.pushToFormArray(key, value.length, arrayIndex);
        }
        for (let i = 0 ;i < value?.length; i++) {
          if (typeof value[i] === 'object' && !Array.isArray(value[i]) && value[i] !== null) {
            this.prepareForm(value[i], fields, i);
          }
          // Maybe a check for array in array should be here
        }
      } else if (value === null) {
        // console.log(key+ ' is null');
      }
    }
  }

  pushToFormArray(name: string, length: number, arrayIndex?: number) {
    let field = this.getModelData(this.model.sections, name);
    // console.log(name)
    while (this.getFormControl(this.form, name, arrayIndex)?.length < length) {
      this.getFormControl(this.form, name, arrayIndex).push(this.formControlService.createField(field));
    }
  }

  getModelData(model: Section[], name: string): Field {
    let field = null;
    for (let i = 0; i < model.length; i++) {
      if (model[i].fields === null) {
        field = this.getModelData(model[i].subSections, name);
        if (field) {
          return field;
        }
      } else {
        field = this.searchSubFields(model[i].fields, name);
        if (field) {
          return field;
        }
      }
    }
    return field;
  }

  searchSubFields(fields: Field[], name: string): Field | null {
    let field = null;
    for (let j = 0; j < fields.length; j++) {
      if(fields[j].name === name) {
        return fields[j];
      } else if (fields[j].subFields?.length > 0) {
        field = this.searchSubFields(fields[j].subFields, name);
        if (field?.name === name)
          return field;
      }
    }
    return null;
  }

  getFormControl(group: UntypedFormGroup | UntypedFormArray, name: string, position?: number): UntypedFormArray {
    let abstractControl = null;
    for (const key in group.controls) {
      abstractControl = group.controls[key];
      if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
        if (key === name) {
          return abstractControl as UntypedFormArray;
        } else if (key !== name) {
          if (abstractControl instanceof UntypedFormArray) {
            if (abstractControl.controls.length > position) {
              abstractControl = this.getFormControl(abstractControl.controls[position] as UntypedFormGroup | UntypedFormArray, name, position);
              if (abstractControl !== null)
                return abstractControl;
            } else {
              abstractControl = null;
            }
          } else {
            abstractControl = this.getFormControl(abstractControl, name, position);
            if (abstractControl !== null)
              return abstractControl;
          }
        }
      } else {
        if (key === name) {
          return abstractControl;
        }
        abstractControl = null;
      }
    }
    return abstractControl;
  }
  /** <-- create additional fields for arrays if needed **/

  /** Generate PDF --> **/
  generatePDF() {
    this.pdfService.generatePDF(this.model, this.payload?.answer, this.form);
  }
  /** <-- Generate PDF **/

  /** other stuff --> **/
  closeError() {
    UIkit.alert('#errorAlert').close();
    setTimeout(() => {
      this.errorMessage = '';
    }, 550);
  }

  closeSuccessAlert() {
    this.timeoutId = setTimeout(() => {
      UIkit.alert('#successAlert').close();
    }, 4000);
    setTimeout(() => {
      this.successMessage = '';
    }, 4550);
  }

  toTop() {
    window.scroll({top: 0, behavior: 'smooth'});
  }

}
